<script lang="ts">
    import {
        Alert,
        Button,
        Fileupload,
        Heading,
        Hr,
        Input,
        Label,
        Modal,
        Select,
        Spinner,
        Toggle
    } from "flowbite-svelte";
    import {getContext, onMount} from "svelte";
    import {getTariffs} from "$lib/calls/tariff";
    import dayjs from "dayjs";
    import {getCostCenters, getUnits} from "$lib/calls/unit";
    import {formatCurrency} from "$lib/utils/currency";
    import {availableQualifications, unitQualifications} from "$lib/calls/qualification";
    import {blur} from "svelte/transition";
    import {getDocumentTemplates} from "$lib/calls/settings";
    import VerticalGrid from "$lib/components/VerticalGrid.svelte";
    import InfoBox from "$items/InfoBox.svelte";
    import {formDataPost, post, retire} from "$lib/api";
    import {goto, invalidateAll} from "$app/navigation";
    import {matchContractVariables} from "$lib/utils/templating/contractVariables";
    import wordDocumentParser from "$lib/utils/templating/wordDocumentParser";
    import {PUBLIC_API_PATH} from "$env/static/public";
    import {globalToast} from "$lib/stores/toast";
    import {globalConfirm} from "$lib/stores/modal";
    import {BuildingOutline, BuildingSolid, CheckCircleSolid, PenSolid} from "flowbite-svelte-icons";
    import type {Contract} from "$lib/types/employee";
    import {tariffs} from "$lib/stores/tariffs";
    import EmailTemplateEditor from "$lib/components/EmailTemplateEditor.svelte";
    import {mailTemplates} from "$lib/stores/mailTemplates";
    import {getEmailTemplates, sendEmailTemplate} from "$lib/calls/mail";
    import {get} from "$lib/api";
    import type {EmailTemplate} from "$lib/types/EmailTemplate";
    import {contractTypes} from "$lib/utils/templating/contractTypes";
    import {findUnmatchedVariables} from "$lib/utils/templating/parseHandlebars";
    import {allUnits} from "$lib/stores/unit";
    import {templateDocuments} from "$lib/stores/templateDocuments";
    import generateContractPreview from "$lib/utils/templating/generateContractPreview";
    import NewContractForm from "$lib/partials/employee/NewContractForm.svelte";
    import Qualifications from "$items/employee/Qualifications.svelte";
    import BankAccount from "$items/employee/BankAccount.svelte";
    import EmployeeInfo from "$items/employee/EmployeeInfo.svelte";
    import TaxAndInsurance from "$items/employee/TaxAndInsurance.svelte";
    import ModalFooter from "$lib/components/ui/ModalFooter.svelte";
    import {type Writable, writable} from "svelte/store";

    export let open = false

    export let employee;

    export let contract: Contract= {
        currentHistory: {
            startDate: dayjs().add(1, 'month').date(1).format('YYYY-MM-DD'),
            endDate: dayjs().year() + '-12-31',

            hiredAs: 'ServicehilfKraft',

        },
        history: undefined
    };

    let formStatus = writable({
        loading: false,
        preview: null,
        formComplete: false,
        triggerPreview: false,
        triggerContract: false
    });



    let doneModal:Writable<boolean> = getContext('doneModal');




    let files:FileList = [];

    let qualifications:any[] = [];



    let mailTemplate:EmailTemplate;



    const sendEmail = async () => {
        try{
            const res = await sendEmailTemplate(mailTemplate.id, {
                fromEmail: 'legal@recrew.de',
                fromName: 'REcrew Legal',
                subject: 'REcrew_Vertrag_zwischen_' + employee.name + ' und REcrew GmbH zur Signatur bereit!',
                entityId: employee.id,
                content: mailTemplate.content.replace(/<% body %>/g, ''),
                toEmail: employee.email,
                toName: employee.name,
                variables: findUnmatchedVariables(mailTemplate.content)
            })
            open = false
            $doneModal = true
        } catch (e) {

        }

    }


    const abort = async () => {
        $formStatus.loading = true;
        if(contract.id){
            await retire(`/employee/${employee.uuid}/contract/${contract.id}?silent=true`)
        }
        $formStatus.triggerPreview = false;
        $formStatus.preview = null;
        $formStatus.loading = false;
    }

    onMount(async () => {
        if($tariffs.length === 0){
            $tariffs = await getTariffs();
        }
        if($allUnits.length === 0){
            $allUnits = await getUnits();
        }

        for (const unit of $allUnits) {
            qualifications = [...qualifications, ...await unitQualifications(unit.id)]
        }
        if($templateDocuments.length === 0){
            $templateDocuments = await getDocumentTemplates()
            console.log({tmpl: $templateDocuments})
        }
        if($mailTemplates.length === 0){
            $mailTemplates = await getEmailTemplates()
        }
        mailTemplate = $mailTemplates.find(x => x.level === 'contractible');


    })

</script>

<Modal  bind:open size="xl">
    <svelte:fragment slot="header">
        <h3 class="text-xl font-semibold text-neutral-600 dark:text-white pl-4 flex items-center">
            <BuildingOutline class="mr-2 w-4"/>
            Neuer Vertrag: {employee.firstName} {employee.lastName}
        </h3>
    </svelte:fragment>
    <div class="mt-3 space-y-3">
        <EmployeeInfo display={false} readOnly/>
        <Qualifications display={false} readOnly/>
        <TaxAndInsurance display={false} readOnly/>
        <BankAccount display={false} readOnly/>
    </div>
    <div class="w-full">

        {#if contract.history}
            <div class="space-y-2 mb-3">
                <span>Email Template</span>
                <Select required bind:value={mailTemplate.id} on:change={(e) => {const id = mailTemplate.id; mailTemplate = null; setTimeout(() => {mailTemplate = $mailTemplates.find(x => x.id === id)})}} items={$mailTemplates.filter(x => x.level === 'contractible').map(x => ({...x, value: x.id}))}/>
            </div>
            {#if mailTemplate}
                <EmailTemplateEditor on:update={e => mailTemplate = e.detail} preview={true} template={mailTemplate} fillData={{Employee: employee, Contract: contract.history[0], Unit: $allUnits.find(x => x.id === employee.unitId)}} />
            {/if}
        {:else}
            <NewContractForm bind:sharedState={formStatus} {employee} bind:contract on:done={() => {$doneModal = true; open = false}} />
        {/if}
    </div>
    <svelte:fragment slot="footer">
        <ModalFooter disabled={$formStatus.loading} showEdit={!!$formStatus.preview} on:abort={() => {abort(); open = false}} on:edit={abort}>
            <svelte:fragment slot="right">

                {#if $formStatus.preview}
                    <Button disabled={$formStatus.loading} class="!text-decoration-none whitespace-nowrap" download="Vertragsangebot_{employee.name.replaceAll(' ','_')}.docx" href={URL.createObjectURL($formStatus.preview)} target="_blank" color="light">Vorschau erzeugter Vertrag</Button>
                    {#if contract.history}
                        <Button disabled={$formStatus.loading} on:click={() => sendEmail()} class="bg-sky-100 text-neutral-600 whitespace-nowrap">Email versenden</Button>
                    {:else}
                        <Button class="bg-sky-100 text-neutral-600 whitespace-nowrap" disabled={$formStatus.loading} on:click={() => $formStatus.triggerContract = true}>Vertrag erstellen</Button>
                    {/if}

                {:else if $formStatus.formComplete}
                    <Button disabled={$formStatus.loading} class="bg-sky-100 text-neutral-600 whitespace-nowrap" on:click={() => $formStatus.triggerPreview = true}>Vertragsvorschau erzeugen</Button>
                {/if}
            </svelte:fragment>
        </ModalFooter>
    </svelte:fragment>
</Modal>